// src/pages/Club/VereineListePage.jsx - Dashboard Card Style
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/VereineListePage.css'; // Neues CSS für Card-Design

const VereineListePage = () => {
  const navigate = useNavigate();
  const [vereine, setVereine] = useState([]);
  const [archivedVereine, setArchivedVereine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('name-asc');
  const [letterFilter, setLetterFilter] = useState('Alle');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [activeListTab, setActiveListTab] = useState('aktiv');
  const [archiveModal, setArchiveModal] = useState({ show: false, vereinId: null, vereinName: '', grund: '' });
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [stats, setStats] = useState({
    gesamt: 0,
    aktive: 0,
    archiviert: 0,
    totalWettkaempfer: 0,
    mitHomepage: 0,
    topStadt: ''
  });

  useEffect(() => {
    fetchVereine();
    fetchArchivedVereine();
    fetchWettkaempferStats();
    fetchAdminStats();
  }, []);

  const fetchVereine = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/vereine', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle different response formats
      let vereineData = [];
      if (data.success && Array.isArray(data.data)) {
        vereineData = data.data;
      } else if (Array.isArray(data)) {
        vereineData = data;
      }

      console.log('🏛️ Vereine geladen:', vereineData.length, 'Beispiel:', vereineData[0]?.name, 'Kämpfer:', vereineData[0]?.anzahl_wettkaempfer);
      setVereine(vereineData);

      // Berechne Statistiken
      setStats(prevStats => ({
        ...prevStats,
        gesamt: vereineData.length,
        aktive: vereineData.filter(v => v.rolle === 'verein' || !v.rolle).length
      }));

    } catch (err) {
      console.error('❌ Fehler beim Laden der Vereine:', err);
      setError('Fehler beim Laden der Vereine: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedVereine = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/vereine/archived', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      let archivedData = [];
      if (data.success && Array.isArray(data.data)) {
        archivedData = data.data;
      } else if (Array.isArray(data)) {
        archivedData = data;
      }

      setArchivedVereine(archivedData);
      setStats(prevStats => ({
        ...prevStats,
        archiviert: archivedData.length
      }));

    } catch (err) {
      console.error('❌ Fehler beim Laden archivierter Vereine:', err);
    }
  };

  const fetchWettkaempferStats = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/wettkaempfer/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const total = data.data?.totalWettkaempfer ?? 0;

      setStats(prevStats => ({
        ...prevStats,
        totalWettkaempfer: total
      }));

    } catch (err) {
      console.error('❌ Fehler beim Laden der Wettkämpfer-Statistik:', err);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/vereine/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return; // Admin-Stats sind optional
      }

      const data = await response.json();

      if (data.success && data.data) {
        setStats(prevStats => ({
          ...prevStats,
          mitHomepage: data.data.vereineMitHomepage || 0,
          topStadt: data.data.byOrt?.[0]?.ort || ''
        }));
      }

    } catch (err) {
      console.error('❌ Fehler beim Laden der Admin-Statistik:', err);
    }
  };

  // Toggle einzelne Karte ein-/ausklappen
  const toggleCard = (vereinId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vereinId)) {
        newSet.delete(vereinId);
      } else {
        newSet.add(vereinId);
      }
      return newSet;
    });
  };

  // Alle Karten ein-/ausklappen
  const toggleAllCards = () => {
    if (expandedCards.size === filteredVereine.length) {
      setExpandedCards(new Set());
    } else {
      setExpandedCards(new Set(filteredVereine.map(v => v.vereins_id)));
    }
  };

  const handleArchive = (vereinId, vereinName) => {
    setArchiveModal({ show: true, vereinId, vereinName, grund: '' });
  };

  const handleArchiveConfirm = async () => {
    const { vereinId, vereinName, grund } = archiveModal;
    setArchiveLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vereine/${vereinId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ grund: grund || 'Archiviert durch Admin' })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();

      if (data.success) {
        setArchiveModal({ show: false, vereinId: null, vereinName: '', grund: '' });
        fetchVereine();
        fetchArchivedVereine();
      } else {
        throw new Error(data.error || 'Archivierung fehlgeschlagen');
      }
    } catch (err) {
      console.error('❌ Fehler beim Archivieren:', err);
      alert('Fehler beim Archivieren: ' + err.message);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handlePermanentDelete = async (vereinId, vereinName) => {
    if (!window.confirm(`ACHTUNG: Verein "${vereinName}" PERMANENT löschen?\n\nDiese Aktion kann NICHT rückgängig gemacht werden!\nAlle Daten inkl. Wettkämpfer und Archiv-Einträge werden gelöscht.`)) {
      return;
    }

    const confirmText = prompt('Bitte geben Sie "LÖSCHEN" ein, um zu bestätigen:');
    if (confirmText !== 'LÖSCHEN') {
      alert('Löschung abgebrochen.');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/vereine/${vereinId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        alert(`✅ Verein "${vereinName}" permanent gelöscht!`);
        fetchVereine();
        fetchArchivedVereine();
      } else {
        throw new Error(data.error || 'Löschung fehlgeschlagen');
      }

    } catch (err) {
      console.error('❌ Fehler beim Löschen:', err);
      alert('Fehler beim Löschen: ' + err.message);
    }
  };

  const handleRestore = async (archivId, vereinName) => {
    if (!window.confirm(`Verein "${vereinName}" aus dem Archiv wiederherstellen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/vereine/archived/${archivId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        alert(`✅ Verein "${vereinName}" erfolgreich wiederhergestellt!`);
        fetchVereine();
        fetchArchivedVereine();
      } else {
        throw new Error(data.error || 'Wiederherstellung fehlgeschlagen');
      }

    } catch (err) {
      console.error('❌ Fehler beim Wiederherstellen:', err);
      alert('Fehler beim Wiederherstellen: ' + err.message);
    }
  };

  const handleVereinClick = (vereinId) => {
    navigate(`/dashboard/vereine/${vereinId}`);
  };

  // Ermittle alle verfügbaren Anfangsbuchstaben
  const availableLetters = React.useMemo(() => {
    const letters = new Set();
    vereine.forEach(verein => {
      if (verein.name) {
        const firstLetter = verein.name.charAt(0).toUpperCase();
        if (firstLetter.match(/[A-Z]/)) {
          letters.add(firstLetter);
        }
      }
    });
    return Array.from(letters).sort();
  }, [vereine]);

  const filteredVereine = vereine
    .filter(verein => {
      // Suchfilter
      const matchesSearch = verein.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        verein.ort?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        verein.ansprechpartner?.toLowerCase().includes(searchTerm.toLowerCase());

      // Buchstabenfilter
      const matchesLetter = letterFilter === 'Alle' ||
        verein.name?.charAt(0).toUpperCase() === letterFilter;

      return matchesSearch && matchesLetter;
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'ort-asc':
          return (a.ort || '').localeCompare(b.ort || '');
        case 'ort-desc':
          return (b.ort || '').localeCompare(a.ort || '');
        case 'newest':
          return new Date(b.erstellt_am || 0) - new Date(a.erstellt_am || 0);
        case 'oldest':
          return new Date(a.erstellt_am || 0) - new Date(b.erstellt_am || 0);
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="vereine-page">
        <div className="page-header">
          <h1>🏛️ Vereine verwalten</h1>
          <p>Zentrale Verwaltung aller registrierten Vereine</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Vereine werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vereine-page">
        <div className="page-header">
          <h1>🏛️ Vereine verwalten</h1>
          <p>Zentrale Verwaltung aller registrierten Vereine</p>
        </div>
        <div className="error-container">
          <h2>❌ Fehler beim Laden</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchVereine}>
            🔄 Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vereine-page">
      {/* ✅ Dashboard-Style Header */}
      <div className="page-header">
        <h1>🏛️ Vereine verwalten</h1>
        <p>Zentrale Verwaltung aller registrierten Vereine</p>
      </div>

      {/* ✅ Statistik-Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏛️</div>
          <div className="stat-content">
            <h3>{stats.gesamt}</h3>
            <p>Vereine gesamt</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🥋</div>
          <div className="stat-content">
            <h3>{stats.totalWettkaempfer}</h3>
            <p>Wettkämpfer</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats.archiviert}</h3>
            <p>Archiviert</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔍</div>
          <div className="stat-content">
            <h3>{filteredVereine.length}</h3>
            <p>Gefiltert</p>
          </div>
        </div>
      </div>

      {/* ✅ Such- und Filter-Bereich */}
      <div className="controls-section">
        <div className="search-container">
          <input
            type="text"
            placeholder="🔍 Vereine durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="sort-container">
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="sort-select"
          >
            <option value="name-asc">🔤 Name (A-Z)</option>
            <option value="name-desc">🔤 Name (Z-A)</option>
            <option value="ort-asc">📍 Ort (A-Z)</option>
            <option value="ort-desc">📍 Ort (Z-A)</option>
            <option value="newest">📅 Neueste zuerst</option>
            <option value="oldest">📅 Älteste zuerst</option>
          </select>
        </div>
        <div className="letter-filter-container">
          <button
            className={`letter-filter-btn ${letterFilter === 'Alle' ? 'active' : ''}`}
            onClick={() => setLetterFilter('Alle')}
          >
            Alle
          </button>
          {availableLetters.map(letter => (
            <button
              key={letter}
              className={`letter-filter-btn ${letterFilter === letter ? 'active' : ''}`}
              onClick={() => setLetterFilter(letter)}
            >
              {letter}
            </button>
          ))}
        </div>
        <div className="actions-container">
          <button
            className="action-button toggle"
            onClick={toggleAllCards}
            title={expandedCards.size === filteredVereine.length ? 'Alle zuklappen' : 'Alle aufklappen'}
          >
            {expandedCards.size === filteredVereine.length ? '▼ Zuklappen' : '▶ Aufklappen'}
          </button>
          <button
            className="action-button primary"
            onClick={() => {
              fetchVereine();
              fetchArchivedVereine();
              fetchWettkaempferStats();
              fetchAdminStats();
            }}
          >
            🔄 Aktualisieren
          </button>
          <button
            className="action-button secondary"
            onClick={() => navigate('/dashboard/vereine/neu')}
          >
            ➕ Neuer Verein
          </button>
        </div>
      </div>

      {/* ✅ TAB-NAVIGATION: Aktiv / Archiviert */}
      <div className="verein-tabs-nav">
        {[
          { id: 'aktiv',      label: `Aktive Vereine (${filteredVereine.length})` },
          { id: 'archiviert', label: `Archiv (${archivedVereine.length})` }
        ].map(tab => (
          <button key={tab.id} className={`verein-tab-btn${activeListTab === tab.id ? ' active' : ''}`} onClick={() => setActiveListTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ✅ TAB: AKTIVE VEREINE */}
      {activeListTab === 'aktiv' && (
        filteredVereine.length === 0 ? (
          <div className="no-results">
            <div className="no-results-content">
              <h3>🔍 Keine Vereine gefunden</h3>
              <p>{searchTerm ? `Keine Vereine entsprechen dem Suchbegriff "${searchTerm}"` : 'Noch keine Vereine registriert'}</p>
              {searchTerm && <button className="clear-search-button" onClick={() => setSearchTerm('')}>❌ Suche löschen</button>}
            </div>
          </div>
        ) : (
          <div className="vereine-grid">
            {filteredVereine.map((verein) => {
              const isExpanded = expandedCards.has(verein.vereins_id);
              return (
                <div key={verein.vereins_id} className={`verein-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                  <div className="verein-card-header-compact" onClick={(e) => { e.stopPropagation(); toggleCard(verein.vereins_id); }}>
                    <div className="verein-card-header-top">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      <h3 className="verein-name">{verein.name}</h3>
                    </div>
                    <div className="quick-actions">
                      <span className="verein-kaempfer">🥋 {verein.anzahl_wettkaempfer || 0}</span>
                      <span className="verein-ort">{verein.ort || ''}</span>
                      <button className="quick-btn" onClick={(e) => { e.stopPropagation(); handleVereinClick(verein.vereins_id); }} title="Details anzeigen">👁️</button>
                      <button className="quick-btn" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/vereine/${verein.vereins_id}/bearbeiten`); }} title="Bearbeiten">✏️</button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="verein-card-expanded">
                      <div className="verein-info-grid">
                        <div className="info-item"><span className="info-label">Wettkämpfer</span><span className="info-value">{verein.anzahl_wettkaempfer || 0}</span></div>
                        <div className="info-item"><span className="info-label">Ansprechpartner</span><span className="info-value">{verein.ansprechpartner || '—'}</span></div>
                        <div className="info-item"><span className="info-label">E-Mail</span><span className="info-value">{verein.email}</span></div>
                        <div className="info-item"><span className="info-label">Telefon</span><span className="info-value">{verein.telefon || '—'}</span></div>
                      </div>
                      <div className="verein-card-actions">
                        <button className="card-action-button primary" onClick={() => handleVereinClick(verein.vereins_id)}>Details</button>
                        <button className="card-action-button secondary" onClick={() => navigate(`/dashboard/vereine/${verein.vereins_id}/bearbeiten`)}>Bearbeiten</button>
                        <button className="card-action-button archive" onClick={() => handleArchive(verein.vereins_id, verein.name)}>Archivieren</button>
                        <button className="card-action-button danger" onClick={() => handlePermanentDelete(verein.vereins_id, verein.name)}>Löschen</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ✅ TAB: ARCHIVIERTE VEREINE */}
      {activeListTab === 'archiviert' && (
        <div>
          {archivedVereine.length === 0 ? (
            <div className="no-results">
              <div className="no-results-content">
                <h3>📦 Kein Archiv</h3>
                <p>Es wurden noch keine Vereine archiviert.</p>
              </div>
            </div>
          ) : (
            <div className="archiv-table-wrap">
              <table className="archiv-table">
                <thead>
                  <tr className="archiv-thead-row">
                    {['Verein', 'Ort', 'E-Mail', 'Archiviert am', 'Grund', 'WK', 'Aktionen'].map(h => (
                      <th key={h} className="archiv-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {archivedVereine.map((verein, idx) => (
                    <tr key={verein.archiv_id} className={`archiv-tr${idx % 2 !== 0 ? ' archiv-tr-alt' : ''}`}>
                      <td className="archiv-td archiv-td-name">{verein.name}</td>
                      <td className="archiv-td">{verein.ort || '—'}</td>
                      <td className="archiv-td">{verein.email || '—'}</td>
                      <td className="archiv-td archiv-td-nowrap">
                        {verein.archiviert_am ? new Date(verein.archiviert_am).toLocaleDateString('de-DE') : '—'}
                      </td>
                      <td className="archiv-td archiv-td-muted">{verein.archivierungsgrund || '—'}</td>
                      <td className="archiv-td archiv-td-center">{verein.anzahl_wettkaempfer || 0}</td>
                      <td className="archiv-td archiv-td-actions">
                        <button className="archiv-btn restore" onClick={() => handleRestore(verein.archiv_id, verein.name)}>Wiederherstellen</button>
                        <button className="archiv-btn danger" onClick={() => handlePermanentDelete(verein.original_vereins_id, verein.name)}>Löschen</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ✅ ARCHIVIERUNGS-MODAL */}
      {archiveModal.show && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1e1e1e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '28px', width: '480px', maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 8px', color: '#fff' }}>📦 Verein archivieren</h3>
            <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
              <strong style={{ color: '#fff' }}>"{archiveModal.vereinName}"</strong> wird archiviert und aus der aktiven Liste entfernt.
            </p>
            <label style={{ display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
              Grund (optional)
            </label>
            <input
              type="text"
              placeholder="z.B. Verein aufgelöst, Inaktiv seit..."
              value={archiveModal.grund}
              onChange={e => setArchiveModal(prev => ({ ...prev, grund: e.target.value }))}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', background: '#161616', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px', marginBottom: '20px' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setArchiveModal({ show: false, vereinId: null, vereinName: '', grund: '' })}
                style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px' }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleArchiveConfirm}
                disabled={archiveLoading}
                style={{ padding: '8px 20px', background: '#ff3333', border: '1px solid #ff3333', borderRadius: '8px', color: '#fff', cursor: archiveLoading ? 'not-allowed' : 'pointer', fontSize: '14px', opacity: archiveLoading ? 0.7 : 1 }}
              >
                {archiveLoading ? 'Archiviert...' : '📦 Archivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VereineListePage;